import type React from 'react';

import classNames from 'classnames';

import type { IconName } from '@helsenorge/designsystem-react/components/Icons/IconNames';
import LazyIcon from '@helsenorge/designsystem-react/components/LazyIcon';

import { HNeventShowCookieBanner } from '@helsenorge/core-cms/cookie-banner/events';
import { APP_COOKIE_SETTINGS_URL, getUseInAppCookieSettings } from '@helsenorge/core-cms/cookie-banner/utils';
import { mapCultureNameToLanguageLocale } from '@helsenorge/core-cms/shared/language';
import { getAbsoluteUrl } from '@helsenorge/core-cms/shared/links';
import type { GlobalFooterSupportHotline, IconLinkDto } from '@helsenorge/core-cms/types/entities/cms.v1entities';
import SafeHTML from '@helsenorge/framework-utils/components/safe-html';
import { setUserLanguage } from '@helsenorge/framework-utils/hn-language';

import styles from './styles.module.scss';

/* Function to fetch footer data from content API  */

/* Function to render the Hotline information as a first row is the first column */
export const renderSupportHotlineLink = (supportHotline: GlobalFooterSupportHotline): React.JSX.Element => {
  const { displayText, displayNumber, schemaNumber, phoneNumberPrefix, phoneNumberSuffix, icon } = supportHotline;

  const footerIconClassNames = classNames(styles['footer__element__icon'], styles['footer__element__icon--alignstart']);

  return (
    <>
      {icon && (
        <span className={footerIconClassNames}>
          <LazyIcon size={32} color="white" iconName={icon as IconName} />
        </span>
      )}
      <a href={`tel:${schemaNumber}`} className={styles['footer__element__link']}>
        <span className={styles['footer__element__text']}>
          {phoneNumberPrefix} {displayNumber} {phoneNumberSuffix}
          <br />
        </span>
        <span className={classNames(styles['footer__element__text'], styles['footer__element__text--small'])}>{displayText}</span>
      </a>
    </>
  );
};

/* Function to render the Hotline information as a first row is the first column */
export const renderSupportHotlineLinkSimplified = (supportHotline: GlobalFooterSupportHotline): React.JSX.Element => {
  const { displayText, displayNumber, schemaNumber } = supportHotline;

  const elementClassNames = classNames(styles['footer__element'], styles['footer__element--simplified']);
  const textElementClassNames = classNames(styles['footer__element__text'], styles['footer__element__text--simplified']);
  const linkElementClassNames = classNames(styles['footer__element__link'], styles['footer__element__link--simplified']);

  return (
    <div className={elementClassNames}>
      <span className={textElementClassNames}>{displayText}</span>
      <a href={`tel:${schemaNumber}`} className={linkElementClassNames}>
        {displayNumber}
      </a>
    </div>
  );
};

/* Function to render the custom text in footer simplified*/
export const renderCustomFooterTextSimplified = (text: string | React.JSX.Element): React.JSX.Element => {
  const textClassNames = classNames(styles['footer__element__text'], styles['footer__element__text--simplified']);
  const elementClassNames = classNames(styles['footer__element'], styles['footer__element--simplified']);

  return (
    <div className={elementClassNames}>
      {typeof text === 'string' ? <SafeHTML html={text} className={textClassNames} /> : <span className={textClassNames}>{text}</span>}
    </div>
  );
};

/* Function to render the simplified footer content */
export const renderSimplifiedFooterContent = (
  simplifiedFootertext?: string,
  supportHotline?: GlobalFooterSupportHotline
): React.JSX.Element | string => {
  if (simplifiedFootertext) {
    return renderCustomFooterTextSimplified(simplifiedFootertext);
  } else if (supportHotline) {
    return renderSupportHotlineLinkSimplified(supportHotline);
  }
  return '';
};

/* Function to render each column of the footer */
export const renderFooterColumn = (
  linkList: Array<IconLinkDto>,
  colIndex: number,
  supportHotline?: GlobalFooterSupportHotline,
  languageLinkList?: Array<IconLinkDto>,
  showCookieBannerText?: string
): React.JSX.Element | null => {
  if ((linkList?.length ?? 0) === 0 && !supportHotline && (languageLinkList?.length ?? 0) === 0) {
    return null;
  }

  const isFirstColumn = colIndex === 0;
  const isSecondColumn = colIndex === 1;

  const handleLinkClick = (link: IconLinkDto): void => {
    const locale = mapCultureNameToLanguageLocale(link.cultureName);
    if (locale) {
      setUserLanguage(locale);
    }
  };

  const columnClassNames = classNames({
    [styles[`footer__column--1`]]: colIndex === 1,
    [styles[`footer__column--2`]]: colIndex === 2,
  });
  const elementClassNames = classNames(styles.footer__element);

  const useInAppCookieSettings = getUseInAppCookieSettings();

  const renderFooterLinks = (footerLinkList: Array<IconLinkDto>, seperator = false): React.ReactNode =>
    footerLinkList?.map((link: IconLinkDto, index) => (
      <li
        className={classNames(
          styles.footer__element,
          seperator && index !== footerLinkList.length - 1 && styles['footer__element--separator']
        )}
        key={index}
      >
        {link.icon && (
          <span className={styles['footer__element__icon']}>
            <LazyIcon size={32} color="white" iconName={link.icon as IconName} />
          </span>
        )}
        <a
          className={styles['footer__element__link']}
          href={getAbsoluteUrl(link)}
          target={link.target ?? undefined}
          rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
          title={link.title ?? undefined}
          onClick={(): void => handleLinkClick(link)}
        >
          <span className={styles['footer__element__text']}>{link.text}</span>
        </a>
      </li>
    ));

  return (
    <div className={columnClassNames} key={colIndex}>
      <ul className={styles['footer__column__innerwrapper']}>
        {!!supportHotline?.displayNumber && isFirstColumn && (
          <li className={elementClassNames}>{renderSupportHotlineLink(supportHotline)}</li>
        )}
        {renderFooterLinks(linkList)}
        {languageLinkList && isFirstColumn && (
          <li className={styles.footer__element}>
            <ul className={styles['footer__language-list']}>{renderFooterLinks(languageLinkList, true)}</ul>
          </li>
        )}
        {isSecondColumn && showCookieBannerText && (
          <li className={elementClassNames}>
            {useInAppCookieSettings ? (
              <a className={styles['footer__element__link']} href={APP_COOKIE_SETTINGS_URL}>
                <span className={styles.footer__element__text}>{showCookieBannerText}</span>
              </a>
            ) : (
              <button
                type="button"
                className={styles.footer__element__button}
                onClick={(): void => HNeventShowCookieBanner()}
                aria-haspopup="dialog"
              >
                <span className={styles.footer__element__text}>{showCookieBannerText}</span>
              </button>
            )}
          </li>
        )}
      </ul>
    </div>
  );
};
